import { useState, useEffect, useCallback } from 'react';
import { getFullDdxForComplaint } from '@healthdesk/shared-services';
import { getCantMissRegistry } from '@healthdesk/shared-utils';

/**
 * Load DDX-Plus knowledge slice for one chief complaint (Firestore + bundled can’t-miss fallback).
 *
 * @param {string | null | undefined} chiefComplaintId
 * @returns {{
 *   chiefComplaint: Object | null,
 *   diagnoses: Object[],
 *   features: Object[],
 *   edges: Object[],
 *   cantMiss: Object | null,
 *   ddxKbLoadStatus: 'firestore' | 'no_chief_document' | 'permission_denied' | null,
 *   loading: boolean,
 *   error: Error | null,
 *   refetch: () => void
 * }}
 */
export function useDdx(chiefComplaintId) {
  const [state, setState] = useState({
    chiefComplaint: null,
    diagnoses: [],
    features: [],
    edges: [],
    cantMiss: null,
    ddxKbLoadStatus: null,
    loading: !!chiefComplaintId,
    error: null,
  });
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    if (!chiefComplaintId) {
      setState({
        chiefComplaint: null,
        diagnoses: [],
        features: [],
        edges: [],
        cantMiss: null,
        ddxKbLoadStatus: null,
        loading: false,
        error: null,
      });
      return undefined;
    }

    setState((s) => ({ ...s, loading: true, error: null }));

    getFullDdxForComplaint(chiefComplaintId)
      .then((bundle) => {
        if (cancelled) return;
        setState({
          chiefComplaint: bundle.chiefComplaint,
          diagnoses: bundle.diagnoses,
          features: bundle.features,
          edges: bundle.edges,
          cantMiss: bundle.cantMiss,
          ddxKbLoadStatus: bundle.ddxKbLoadStatus ?? null,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          chiefComplaint: null,
          diagnoses: [],
          features: [],
          edges: [],
          cantMiss: getCantMissRegistry(chiefComplaintId),
          ddxKbLoadStatus: null,
          loading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [chiefComplaintId, tick]);

  return {
    chiefComplaint: state.chiefComplaint,
    diagnoses: state.diagnoses,
    features: state.features,
    edges: state.edges,
    cantMiss: state.cantMiss,
    ddxKbLoadStatus: state.ddxKbLoadStatus,
    loading: state.loading,
    error: state.error,
    refetch,
  };
}
